import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Repeat, Mic, Camera, Type, Utensils } from "lucide-react";

function localDayISO() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const TEAL = "#0D9488";
const TEAL_D = "#0F766E";
const TEAL_SOFT = "#F0FDFA";

const SOURCE_META = {
  voice: { icon: Mic,    label: "Voice",  tint: "bg-sky-50 text-sky-700 border-sky-200" },
  photo: { icon: Camera, label: "Photo",  tint: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200" },
  text:  { icon: Type,   label: "Text",   tint: "bg-teal-50 text-teal-700 border-teal-200" },
};

/**
 * A friendly card row of the user's last 3 AI-parsed meals with a one-tap "log again" pill.
 * Renders nothing when there's no history (empty state suppressed to avoid noise).
 */
export default function RecentAIParsesRibbon({ refreshKey = 0, onRelog }) {
  const [entries, setEntries] = useState([]);
  const [reloggingId, setReloggingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/nutrition/recent-ai", { params: { limit: 3 } });
      setEntries(data.entries || []);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (entries.length === 0) return null;

  const relog = async (e) => {
    setReloggingId(e.id);
    try {
      await api.post("/nutrition", {
        calories: e.calories,
        protein_g: e.protein_g,
        carbs_g: e.carbs_g,
        fat_g: e.fat_g,
        sugar_g: e.sugar_g,
        fiber_g: e.fiber_g,
        sodium_mg: e.sodium_mg,
        meal_group: e.meal_group,
        source: e.source,
        note: e.note,
        thumbnail_b64: e.thumbnail_b64 || null,
        day: localDayISO(),
      });
      toast.success(`Re-logged: ${e.note?.slice(0, 40) || "meal"}`);
      onRelog?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setReloggingId(null);
    }
  };

  return (
    <div
      className="border border-teal-200 bg-teal-50/40 rounded-xl p-5"
      data-testid="recent-ai-parses-ribbon"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: TEAL_SOFT, color: TEAL }}
          >
            <Sparkles className="w-4 h-4" strokeWidth={2.2} />
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: TEAL }}>
              Recent AI Parses
            </div>
            <div className="text-sm text-slate-700 mt-0.5">Tap a meal to log it again in one shot.</div>
          </div>
        </div>
        <div className="text-[11px] text-slate-500 tabular-nums">
          Last {entries.length} · past 60 entries
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {entries.map((e) => {
          const meta = SOURCE_META[e.source] || SOURCE_META.text;
          const Icon = meta.icon;
          const busy = reloggingId === e.id;
          const initials = (e.note || "meal").trim().slice(0, 2).toUpperCase();
          return (
            <div
              key={e.id}
              className="group relative flex flex-col text-left border border-slate-200 bg-white rounded-xl p-3.5 hover:border-teal-400 hover:shadow-sm transition-all"
              data-testid={`recent-ai-relog-${e.id}`}
            >
              {/* header: thumb + source + date */}
              <div className="flex items-center gap-2.5 mb-2.5">
                {e.thumbnail_b64 ? (
                  <img
                    src={e.thumbnail_b64}
                    alt=""
                    className="w-10 h-10 rounded-lg object-cover shrink-0 border border-slate-200"
                    data-testid={`recent-ai-thumb-${e.id}`}
                    loading="lazy"
                  />
                ) : (
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 font-semibold text-sm text-slate-600"
                    style={{ background: "linear-gradient(135deg,#F1F5F9 0%,#E2E8F0 100%)" }}
                    aria-hidden
                    data-testid={`recent-ai-thumb-${e.id}`}
                  >
                    {initials ? (
                      initials
                    ) : (
                      <Utensils className="w-4 h-4" />
                    )}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className={`inline-flex items-center gap-1 border rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${meta.tint}`}>
                    <Icon className="w-2.5 h-2.5" /> {meta.label}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    {new Date(e.logged_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </div>
                </div>
              </div>

              {/* meal note */}
              <div className="text-sm text-slate-900 font-medium leading-snug mb-2 line-clamp-2 min-h-[2.5em]">
                {e.note || "Untitled meal"}
              </div>

              {/* macros */}
              <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-[11px] mb-3">
                <span className="text-emerald-700 tabular-nums font-semibold">{Math.round(e.calories)}<span className="text-slate-400 font-normal ml-0.5">kcal</span></span>
                {e.protein_g > 0 && <span className="text-rose-600 tabular-nums font-semibold">{Math.round(e.protein_g)}<span className="text-slate-400 font-normal ml-0.5">P</span></span>}
                {e.carbs_g > 0 && <span className="text-blue-600 tabular-nums font-semibold">{Math.round(e.carbs_g)}<span className="text-slate-400 font-normal ml-0.5">C</span></span>}
                {e.fat_g > 0 && <span className="text-orange-600 tabular-nums font-semibold">{Math.round(e.fat_g)}<span className="text-slate-400 font-normal ml-0.5">F</span></span>}
              </div>

              {/* log again pill */}
              <button
                type="button"
                onClick={() => relog(e)}
                disabled={busy}
                className="inline-flex items-center justify-center gap-1.5 rounded-full py-1.5 text-xs font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ background: busy ? TEAL_D : TEAL, color: "#FFFFFF" }}
                onMouseEnter={(el) => { if (!busy) el.currentTarget.style.background = TEAL_D; }}
                onMouseLeave={(el) => { if (!busy) el.currentTarget.style.background = TEAL; }}
                data-testid={`recent-ai-relog-btn-${e.id}`}
                aria-label={`Log this meal again: ${e.note || "meal"}`}
              >
                <Repeat className="w-3.5 h-3.5" />
                {busy ? "Logging…" : "Log again"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
